import * as React from "react";

import { AxiosResponse } from "axios";

import { AjaxManager } from "../utils/ajax";
import { set, assertNever } from "../utils/obj";

type Primitive = string | number | symbol;

function fromEntries<V>(entries: [Primitive, V][]): Record<string, V> {
    const result = {} as Record<Primitive, V>;
    for (const [key, value] of entries) {
        result[String(key)] = value;
    }
    return result;
}

class AsyncMultiComponent extends React.PureComponent<
    AsyncMultiComponentProps,
    AsyncMultiComponentState
> {
    constructor(props: AsyncMultiComponentProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
            captureHashOnce: false,
        };
    }

    reload = (): Promise<void> => {
        if (this.state.status.type !== "SUCCESS") {
            throw new Error(
                "AsyncComponent should be in SUCCESS status to be reloaded"
            );
        }
        const { ajaxManager, promises, _overrideErrorHandlers } = this.props;
        const errorHandlers: [number, () => void][] =
            _overrideErrorHandlers?.map((code) => [code, () => {}]) ?? [];
        const onErrorCode = fromEntries(errorHandlers);

        return ajaxManager
            .ajaxMulti(promises, {
                component: this,
                initialStatusValue: "SUCCESS",
                inFlightStatus: {
                    type: "RELOADING",
                    data: this.state.status.data,
                },
                getSuccessStatus: (data) => ({
                    type: "SUCCESS" as const,
                    data,
                }),
                getErrorStatus: (code) => ({
                    type: "ERROR" as const,
                    code,
                }),
                onErrorCode,
            })
            .then(() => {
                if (this.props.captureHash) {
                    this.setState({ captureHashOnce: true });
                }
            });
    };

    private load = () => {
        const { ajaxManager, promises, _overrideErrorHandlers } = this.props;
        const errorHandlers: [number, () => void][] =
            _overrideErrorHandlers?.map((code) => [code, () => {}]) ?? [];
        const onErrorCode = fromEntries(errorHandlers);

        ajaxManager
            .ajaxMulti(promises, {
                component: this,
                initialStatusValue: "DEFAULT",
                inFlightStatus: { type: "DEFAULT" },
                getSuccessStatus: (args) => ({
                    type: "SUCCESS" as const,
                    data: args,
                }),
                getErrorStatus: (code) => ({
                    type: "ERROR" as const,
                    code,
                }),
                onErrorCode,
            })
            .then(() => {
                if (this.props.captureHash) {
                    this.setState({ captureHashOnce: true });
                }
            });
    };
    private handleHashCapture = () => {
        const hash = window.location.hash;
        if (hash) {
            this.setState(
                {
                    captureHashOnce: false,
                },
                () => {
                    setTimeout(() => {
                        window.location.hash = "";
                        window.location.hash = hash;
                    }, 10);
                }
            );
        }
    };
    componentDidMount = () => {
        this.load();
    };
    render = () => {
        const { status } = this.state;
        switch (status.type) {
            case "DEFAULT":
            case "LOADING":
                return this.props.renderLoading();
            case "SUCCESS":
            case "RELOADING": {
                if (this.state.captureHashOnce) {
                    this.handleHashCapture();
                }
                return this.props.renderContent(status.data);
            }
            case "ERROR": {
                return this.props.renderError(status.code);
            }
            default:
                return assertNever(status);
        }
    };
    getData = (): Array<unknown> => {
        const { status } = this.state;
        switch (status.type) {
            case "DEFAULT":
            case "LOADING":
            case "ERROR": {
                throw new Error(`Cannot get data in status ${status.type}`);
            }
            case "SUCCESS":
            case "RELOADING": {
                return status.data;
            }
            default:
                return assertNever(status);
        }
    };
    updateData = (index: number, data: unknown): Promise<void> => {
        const { status } = this.state;
        switch (status.type) {
            case "DEFAULT":
            case "LOADING":
            case "RELOADING":
            case "ERROR": {
                throw new Error(`Cannot update data in status ${status.type}`);
            }
            case "SUCCESS": {
                return new Promise((resolve) => {
                    const newData = status.data.slice();
                    if (!(index >= 0 && index < newData.length)) {
                        throw new Error(`Invalid index ${index}`);
                    }
                    newData[index] = data;
                    this.setState(
                        {
                            status: set(status, {
                                data: newData,
                            }),
                        },
                        () => resolve()
                    );
                });
            }
            default:
                return assertNever(status);
        }
    };
}

interface AsyncMultiComponentProps {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promises: Array<() => Promise<AxiosResponse<unknown>>>;
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (data: Array<unknown>) => React.ReactNode;
    captureHash?: boolean;

    _overrideErrorHandlers?: number[];
}

type AsyncMultiComponentStatus =
    | {
          type: "SUCCESS";
          data: Array<unknown>;
      }
    | {
          type: "ERROR";
          code: number | undefined;
      }
    | {
          type: "RELOADING";
          data: Array<unknown>;
      }
    | { type: "DEFAULT" }
    | { type: "LOADING" };

interface AsyncMultiComponentState {
    status: AsyncMultiComponentStatus;
    captureHashOnce: boolean;
}

class AsyncComponent<D, P = object> extends React.PureComponent<
    AsyncComponentProps<D, P> & P
> {
    private multiComponent = React.createRef<AsyncMultiComponent>();
    render = () => {
        const {
            promise,
            renderContent: _a,
            onComponentDidUpdate: _b,
            ...sharedProps
        } = this.props;
        return (
            <AsyncMultiComponent
                ref={this.multiComponent}
                {...sharedProps}
                promises={[promise]}
                renderContent={this.renderContent}
            />
        );
    };
    componentDidUpdate = (prevProps: AsyncComponentProps<D, P> & P) => {
        if (this.props.onComponentDidUpdate) {
            this.props.onComponentDidUpdate(this, prevProps);
        }
    };
    reload = () => {
        if (this.multiComponent.current) {
            this.multiComponent.current.reload();
        }
    };
    private renderContent = (data: Array<unknown>) => {
        return this.props.renderContent(data[0] as D);
    };
    getData = (): D | undefined => {
        return this.multiComponent.current?.getData()[0] as D;
    };
    updateData = (data: D): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(0, data) ||
            Promise.reject(new Error())
        );
    };
}

interface AsyncComponentProps<D, P = object> {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promise: () => Promise<AxiosResponse<D>>;
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (data: D) => React.ReactNode;
    captureHash?: boolean;
    onComponentDidUpdate?: (
        asyncComponent: AsyncComponent<D, P>,
        prevProps: AsyncComponentProps<D, P> & P
    ) => void;

    _overrideErrorHandlers?: number[];
}

class AsyncComponent2<D1, D2, P = object> extends React.PureComponent<
    AsyncComponent2Props<D1, D2, P> & P
> {
    private multiComponent = React.createRef<AsyncMultiComponent>();
    render = () => {
        const {
            renderContent: _a,
            onComponentDidUpdate: _b,
            ...sharedProps
        } = this.props;
        return (
            <AsyncMultiComponent
                ref={this.multiComponent}
                {...sharedProps}
                renderContent={this.renderContent}
            />
        );
    };
    componentDidUpdate = (prevProps: AsyncComponent2Props<D1, D2, P> & P) => {
        if (this.props.onComponentDidUpdate) {
            this.props.onComponentDidUpdate(this, prevProps);
        }
    };
    reload = () => {
        if (this.multiComponent.current) {
            this.multiComponent.current.reload();
        }
    };
    private renderContent = (data: Array<unknown>) => {
        return this.props.renderContent(data[0] as D1, data[1] as D2);
    };
    getData = (): [D1, D2] | undefined => {
        return this.multiComponent.current?.getData() as [D1, D2];
    };
    updateData1 = (data1: D1): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(0, data1) ||
            Promise.reject(new Error("Failed to update data1"))
        );
    };
    updateData2 = (data2: D2): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(1, data2) ||
            Promise.reject(new Error("Failed to update data2"))
        );
    };
}

interface AsyncComponent2Props<D1, D2, P = object> {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promises: [
        () => Promise<AxiosResponse<D1>>,
        () => Promise<AxiosResponse<D2>>,
    ];
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (data1: D1, data2: D2) => React.ReactNode;
    captureHash?: boolean;
    onComponentDidUpdate?: (
        asyncComponent: AsyncComponent2<D1, D2, P>,
        prevProps: AsyncComponent2Props<D1, D2, P> & P
    ) => void;

    _overrideErrorHandlers?: number[];
}

class AsyncComponent3<D1, D2, D3, P = object> extends React.PureComponent<
    AsyncComponent3Props<D1, D2, D3, P> & P
> {
    private multiComponent = React.createRef<AsyncMultiComponent>();
    render = () => {
        const {
            renderContent: _a,
            onComponentDidUpdate: _b,
            ...sharedProps
        } = this.props;
        return (
            <AsyncMultiComponent
                ref={this.multiComponent}
                {...sharedProps}
                renderContent={this.renderContent}
            />
        );
    };
    componentDidUpdate = (
        prevProps: AsyncComponent3Props<D1, D2, D3, P> & P
    ) => {
        if (this.props.onComponentDidUpdate) {
            this.props.onComponentDidUpdate(this, prevProps);
        }
    };
    reload = () => {
        if (this.multiComponent.current) {
            this.multiComponent.current.reload();
        }
    };
    private renderContent = (data: Array<unknown>) => {
        return this.props.renderContent(
            data[0] as D1,
            data[1] as D2,
            data[2] as D3
        );
    };
    getData = (): [D1, D2, D3] | undefined => {
        return this.multiComponent.current?.getData() as [D1, D2, D3];
    };
    updateData1 = (data1: D1): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(0, data1) ||
            Promise.reject(new Error("Failed to update data1"))
        );
    };
    updateData2 = (data2: D2): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(1, data2) ||
            Promise.reject(new Error("Failed to update data2"))
        );
    };
    updateData3 = (data3: D3): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(2, data3) ||
            Promise.reject(new Error("Failed to update data3"))
        );
    };
}

interface AsyncComponent3Props<D1, D2, D3, P = object> {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promises: [
        () => Promise<AxiosResponse<D1>>,
        () => Promise<AxiosResponse<D2>>,
        () => Promise<AxiosResponse<D3>>,
    ];
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (data1: D1, data2: D2, data3: D3) => React.ReactNode;
    captureHash?: boolean;
    onComponentDidUpdate?: (
        asyncComponent: AsyncComponent3<D1, D2, D3, P>,
        prevProps: AsyncComponent3Props<D1, D2, D3, P> & P
    ) => void;

    _overrideErrorHandlers?: number[];
}

class AsyncComponent4<D1, D2, D3, D4, P = object> extends React.PureComponent<
    AsyncComponent4Props<D1, D2, D3, D4, P> & P
> {
    private multiComponent = React.createRef<AsyncMultiComponent>();
    render = () => {
        const {
            renderContent: _a,
            onComponentDidUpdate: _b,
            ...sharedProps
        } = this.props;
        return (
            <AsyncMultiComponent
                ref={this.multiComponent}
                {...sharedProps}
                renderContent={this.renderContent}
            />
        );
    };
    componentDidUpdate = (
        prevProps: AsyncComponent4Props<D1, D2, D3, D4, P> & P
    ) => {
        if (this.props.onComponentDidUpdate) {
            this.props.onComponentDidUpdate(this, prevProps);
        }
    };
    reload = (): Promise<void> => {
        if (this.multiComponent.current) {
            return this.multiComponent.current.reload();
        }
        return Promise.reject(new Error("Failed to reload"));
    };
    private renderContent = (data: Array<unknown>) => {
        return this.props.renderContent(
            data[0] as D1,
            data[1] as D2,
            data[2] as D3,
            data[3] as D4
        );
    };
    getData = (): [D1, D2, D3, D4] | undefined => {
        return this.multiComponent.current?.getData() as [D1, D2, D3, D4];
    };
    updateData1 = (data1: D1): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(0, data1) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData2 = (data2: D2): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(1, data2) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData3 = (data3: D3): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(2, data3) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData4 = (data4: D4): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(3, data4) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
}

interface AsyncComponent4Props<D1, D2, D3, D4, P = object> {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promises: [
        () => Promise<AxiosResponse<D1>>,
        () => Promise<AxiosResponse<D2>>,
        () => Promise<AxiosResponse<D3>>,
        () => Promise<AxiosResponse<D4>>,
    ];
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (
        data1: D1,
        data2: D2,
        data3: D3,
        data4: D4
    ) => React.ReactNode;
    captureHash?: boolean;
    onComponentDidUpdate?: (
        asyncComponent: AsyncComponent4<D1, D2, D3, D4, P>,
        prevProps: AsyncComponent4Props<D1, D2, D3, D4, P> & P
    ) => void;

    _overrideErrorHandlers?: number[];
}

class AsyncComponent5<
    D1,
    D2,
    D3,
    D4,
    D5,
    P = object,
> extends React.PureComponent<AsyncComponent5Props<D1, D2, D3, D4, D5, P> & P> {
    private multiComponent = React.createRef<AsyncMultiComponent>();
    render = () => {
        const {
            renderContent: _a,
            onComponentDidUpdate: _b,
            ...sharedProps
        } = this.props;
        return (
            <AsyncMultiComponent
                ref={this.multiComponent}
                {...sharedProps}
                renderContent={this.renderContent}
            />
        );
    };
    componentDidUpdate = (
        prevProps: AsyncComponent5Props<D1, D2, D3, D4, D5, P> & P
    ) => {
        if (this.props.onComponentDidUpdate) {
            this.props.onComponentDidUpdate(this, prevProps);
        }
    };
    reload = () => {
        if (this.multiComponent.current) {
            this.multiComponent.current.reload();
        }
    };
    private renderContent = (data: Array<unknown>) => {
        return this.props.renderContent(
            data[0] as D1,
            data[1] as D2,
            data[2] as D3,
            data[3] as D4,
            data[4] as D5
        );
    };
    getData = (): [D1, D2, D3, D4, D5] | undefined => {
        return this.multiComponent.current?.getData() as [D1, D2, D3, D4, D5];
    };
    updateData1 = (data1: D1): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(0, data1) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData2 = (data2: D2): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(1, data2) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData3 = (data3: D3): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(2, data3) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData4 = (data4: D4): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(3, data4) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
    updateData5 = (data5: D5): Promise<void> => {
        return (
            this.multiComponent.current?.updateData(4, data5) ||
            Promise.reject(new Error("Failed to update data"))
        );
    };
}

interface AsyncComponent5Props<D1, D2, D3, D4, D5, P = object> {
    // Forces the full reload via unmount when the key changes
    key: string;
    ajaxManager: AjaxManager;
    promises: [
        () => Promise<AxiosResponse<D1>>,
        () => Promise<AxiosResponse<D2>>,
        () => Promise<AxiosResponse<D3>>,
        () => Promise<AxiosResponse<D4>>,
        () => Promise<AxiosResponse<D5>>,
    ];
    // We use rendering callbacks to avoid the scan of children and
    // children with partial props setup which doesn't typecheck well.
    renderLoading: () => React.ReactNode;
    renderError: (code: number | undefined) => React.ReactNode;
    renderContent: (
        data1: D1,
        data2: D2,
        data3: D3,
        data4: D4,
        data5: D5
    ) => React.ReactNode;
    captureHash?: boolean;
    onComponentDidUpdate?: (
        asyncComponent: AsyncComponent5<D1, D2, D3, D4, D5, P>,
        prevProps: AsyncComponent5Props<D1, D2, D3, D4, D5, P> & P
    ) => void;

    _overrideErrorHandlers?: number[];
}

export {
    AsyncComponent,
    type AsyncComponentProps,
    AsyncComponent2,
    type AsyncComponent2Props,
    AsyncComponent3,
    type AsyncComponent3Props,
    AsyncComponent4,
    type AsyncComponent4Props,
    AsyncComponent5,
    type AsyncComponent5Props,
};
